import { inject, Injectable, signal } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { Params, Router } from '@angular/router';
import { Subject } from 'rxjs';
import { NgbDate } from '@ng-bootstrap/ng-bootstrap';
import dayjs from 'dayjs';

import { environment } from '../../../../environments/environment';
import { APP_ROUTES } from '../../constants';
import { SearchFlightSelectionType } from '../../enums';
import { Airport, SearchFlight } from '../../models/app';

import { AppStore } from '../../store';
import { AppService } from './app.service';
import { ToastService } from './toast.service';

@Injectable({
  providedIn: 'root',
})
export class SearchService {
  private _fb = inject(FormBuilder);
  private _router = inject(Router);
  private _appService = inject(AppService);
  private _toastService = inject(ToastService);
  private _appStore = inject(AppStore);
  private _search$ = new Subject<SearchFlight>();
  private _navigate$ = new Subject<SearchFlight>();
  private _isLoading = signal(false);

  readonly search$ = this._search$.asObservable();
  readonly navigate$ = this._navigate$.asObservable();
  readonly isLoading = this._isLoading.asReadonly();
  readonly SelectionType = SearchFlightSelectionType;
  readonly selectionType = signal(SearchFlightSelectionType.from);

  dataForm!: FormGroup;

  constructor() {
    this.initForm();
  }

  get f() {
    return this.dataForm.controls;
  }

  set isLoadingFlights(value: boolean) {
    this._isLoading.set(value);
  }

  updateAirport(selectionType: SearchFlightSelectionType, airport: Airport) {
    const { from, to } = this.dataForm.value;
    switch (selectionType) {
      case SearchFlightSelectionType.from:
        this.dataForm.patchValue({
          from: airport,
        });
        if (to.code === airport.code) {
          this.dataForm.patchValue({
            to: from,
          });
        }
        break;
      case SearchFlightSelectionType.to:
        this.dataForm.patchValue({
          to: airport,
        });
        if (from.code === airport.code) {
          this.dataForm.patchValue({
            from: to,
          });
        }
        break;
    }
  }

  updateDate(selectionType: SearchFlightSelectionType, value: NgbDate) {
    const { date, returnDate } = this.dataForm.value;
    const parsedDate = dayjs(
      `${value.year}-${value.month}-${value.day}`,
    ).toDate();
    switch (selectionType) {
      case SearchFlightSelectionType.from:
        this.dataForm.patchValue({
          date: parsedDate,
        });
        if (returnDate && dayjs(returnDate).isBefore(parsedDate)) {
          this.dataForm.patchValue({
            returnDate: parsedDate,
          });
        }
        break;
      case SearchFlightSelectionType.to:
        this.dataForm.patchValue({
          returnDate: parsedDate,
        });
        if (date && dayjs(date).isAfter(parsedDate)) {
          this.dataForm.patchValue({
            date: parsedDate,
          });
        }
        break;
    }
  }

  swapAirport() {
    const { from, to } = this.dataForm.value;
    this.dataForm.patchValue({
      from: to,
      to: from,
    });
  }

  search(): void {
    if (environment.debug) {
      console.log('[ON_SEARCH]', this.dataForm.value);
    }
    if (this.dataForm.valid && this.validateForm()) {
      this._search$.next(this.dataForm.value);
    }
  }

  navigateToSelectFlight() {
    if (environment.debug) {
      console.log('[ON_NAVIGATE]', this.dataForm.value);
    }
    this._navigate$.next(this.dataForm.value);
    this._router.navigateByUrl(
      this.createSelectFlightRoute(this.dataForm.value),
    );
  }

  updateFromParams(params: Params, queryParams: Params) {
    const { from, to, date } = params;
    const { adult, child, infant, code } = queryParams;
    let { returnDate, returndate } = queryParams;
    if (!returnDate && returndate) {
      returnDate = returndate;
    }

    if (from === to) {
      to === 'HAN' ? 'SGN' : 'HAN';
    }

    let parsedDate = dayjs(date, 'DDMMYYYY').isValid()
      ? dayjs(date, 'DDMMYYYY').toDate()
      : dayjs().toDate();

    let parsedReturnDate = returnDate
      ? dayjs(returnDate, 'DDMMYYYY').isValid()
        ? dayjs(returnDate, 'DDMMYYYY').toDate()
        : dayjs(parsedDate).add(8, 'days').toDate()
      : dayjs(parsedDate).add(8, 'days').toDate();

    if (returnDate && dayjs(parsedDate).isAfter(parsedReturnDate)) {
      parsedReturnDate = parsedDate;
    }

    const value = {
      roundTrip: !!returnDate,
      from: this._appStore.getAirport(from),
      to: this._appStore.getAirport(to),
      date: parsedDate,
      returnDate: parsedReturnDate,
      adult: adult ? Number(adult) : 1,
      child: child ? Number(child) : 0,
      infant: infant ? Number(infant) : 0,
      code,
    };

    this.initForm(value);
    this.search();
  }

  private createSelectFlightRoute(value: Partial<SearchFlight>) {
    const queryParams = {
      adult: value.adult,
    } as {
      adult: number;
      child?: number;
      infant?: number;
      returnDate?: string;
    };
    if (value.child) queryParams.child = value.child;
    if (value.infant) queryParams.infant = value.infant;
    if (value.roundTrip) {
      queryParams.returnDate = dayjs(value.returnDate).format('DDMMYYYY');
    }

    const routeToRedirect = this._router.createUrlTree(
      [
        APP_ROUTES.selectFlight,
        value.from!.code,
        value.to!.code,
        dayjs(value.date).format('DDMMYYYY'),
      ],
      {
        queryParams,
      },
    );

    return routeToRedirect;
  }

  private validateForm(): boolean {
    /**
     * Không giới hạn trẻ em trên 6 tuổi:
     * Một người lớn có thể đi cùng nhiều trẻ em từ 6 tuổi trở lên mà không bị giới hạn số lượng.
     *
     * Quy định về trẻ em dưới 6 tuổi:
     * Một người lớn có thể đi cùng tối đa 1 trẻ sơ sinh và 1 trẻ em dưới 6 tuổi, hoặc 2 trẻ em dưới 6 tuổi.
     * Nếu số lượng trẻ em dưới 6 tuổi đi cùng vượt quá quy định, cần liên hệ với hãng hàng không để đặt dịch vụ tiếp viên đi cùng.
     *
     * Quy định về Trẻ em từ 12 tuổi trở lên:
     * Được coi là hành khách người lớn và áp dụng giá vé tương ứng.
     */

    const { adult, child, infant } = this.dataForm.value;

    if (adult < 1) {
      this._toastService.showDanger(
        'Số lượng người lớn phải lớn hơn 0',
        'Tìm chuyến bay',
      );
      return false;
    }

    if (infant > adult) {
      this._toastService.showDanger(
        'Số lượng trẻ em không được lớn hơn số lượng người lớn',
        'Tìm chuyến bay',
      );
      return false;
    }

    if (adult + child + infant > 9) {
      this._toastService.showDanger(
        'Tổng số hành khách phải ít hơn 9',
        'Tìm chuyến bay',
      );
      return false;
    }

    return true;
  }

  private initForm(value?: Partial<SearchFlight>) {
    this.dataForm = this._fb.group({
      roundTrip: [value?.roundTrip?.toString() === 'true' ? true : false],
      from: [
        value?.from ??
          this._appStore.getAirport('SGN') ??
          this._appService.getDefaultAirport('SGN'),
      ],
      to: [
        value?.to ??
          this._appStore.getAirport('HAN') ??
          this._appService.getDefaultAirport('HAN'),
      ],
      date: [value?.date ?? dayjs().add(3, 'day').toDate()],
      returnDate: [value?.returnDate ?? dayjs().add(11, 'days').toDate()],
      adult: [value?.adult ?? 1],
      child: [value?.child ?? 0],
      infant: [value?.infant ?? 0],
      code: [value?.code],
    });
  }
}
